/**
 * Funding form with validation and storage integration
 */
import { Storage } from '../db/storage.js';

export class FundingForm {
  #form;
  #submitBtn;
  #successOverlay;
  #newAppBtn;
  #abortController = new AbortController();

  constructor() {
    this.#form = document.getElementById('fundingForm');
    this.#submitBtn = this.#form?.querySelector('.funding-submit-btn');
    this.#successOverlay = document.getElementById('fundingSuccess');
    this.#newAppBtn = document.getElementById('fundingNewApplication');

    if (!this.#form) return;

    this.#init();
    this.#updateStats();
  }

  #init() {
    const signal = this.#abortController.signal;

    this.#form.addEventListener('submit', (e) => this.#handleSubmit(e), { signal });

    // Real-time validation on blur, clear errors on input
    const inputs = this.#form.querySelectorAll('input, textarea');
    inputs.forEach(input => {
      input.addEventListener('blur', () => this.#validateField(input), { signal });
      input.addEventListener('input', () => this.#clearError(input), { signal });
    });

    // "Submit Another Application" button
    if (this.#newAppBtn) {
      this.#newAppBtn.addEventListener('click', () => this.#resetForm(), { signal });
    }
  }

  #validateField(field) {
    const errorEl = document.getElementById(`${field.id}Error`);
    let message = '';

    if (field.validity.valueMissing) {
      message = 'This field is required';
    } else if (field.validity.typeMismatch) {
      if (field.type === 'email') {
        message = 'Please enter a valid email address';
      } else if (field.type === 'url') {
        message = 'Please enter a valid URL';
      }
    } else if (field.validity.tooShort) {
      message = `Minimum ${field.minLength} characters required`;
    } else if (field.validity.tooLong) {
      message = `Maximum ${field.maxLength} characters allowed`;
    } else if (field.validity.rangeUnderflow) {
      message = 'Amount must be at least 1 DLY';
    } else if (field.validity.patternMismatch) {
      if (field.name === 'wallet') {
        message = 'Enter a valid ERC-20 wallet address (0x followed by 40 hex characters)';
      } else {
        message = 'Invalid format';
      }
    }

    if (message && errorEl) {
      errorEl.textContent = message;
      field.setAttribute('aria-invalid', 'true');
      return false;
    } else {
      if (errorEl) errorEl.textContent = '';
      field.setAttribute('aria-invalid', 'false');
      return true;
    }
  }

  #clearError(field) {
    const errorEl = document.getElementById(`${field.id}Error`);
    if (errorEl) errorEl.textContent = '';
    field.setAttribute('aria-invalid', 'false');
  }

  async #handleSubmit(e) {
    e.preventDefault();

    // Validate all fields
    const inputs = this.#form.querySelectorAll('input[required], textarea[required]');
    let isValid = true;
    let firstInvalid = null;

    inputs.forEach(input => {
      if (!this.#validateField(input)) {
        isValid = false;
        if (!firstInvalid) firstInvalid = input;
      }
    });

    // Also validate optional fields that have values
    const optionalInputs = this.#form.querySelectorAll('input:not([required]), textarea:not([required])');
    optionalInputs.forEach(input => {
      if (input.value.trim()) {
        if (!this.#validateField(input)) {
          isValid = false;
          if (!firstInvalid) firstInvalid = input;
        }
      }
    });

    if (!isValid) {
      firstInvalid?.focus();
      return;
    }

    this.#setLoading(true);

    try {
      // Gather form data
      const requestData = {
        projectName: this.#form.projectName.value.trim(),
        applicantName: this.#form.applicantName.value.trim(),
        email: this.#form.email.value.trim(),
        description: this.#form.description.value.trim(),
        amount: parseFloat(this.#form.amount.value),
        walletAddress: this.#form.wallet.value.trim(),
        documentLink: this.#form.documentLink.value.trim() || null
      };

      // Brief delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 800));

      // Save to storage
      Storage.addFundingRequest(requestData);

      // Show success
      this.#showSuccess();
      this.#updateStats();
    } catch (error) {
      console.error('[FundingForm] Submission error:', error);
      this.#setError();
    } finally {
      this.#setLoading(false);
    }
  }

  #setLoading(isLoading) {
    if (!this.#submitBtn) return;
    this.#submitBtn.disabled = isLoading;
    this.#submitBtn.textContent = isLoading ? 'Submitting...' : 'Submit Application →';
  }

  #setError() {
    if (!this.#submitBtn) return;
    this.#submitBtn.style.borderColor = '#ff6b6b';
    this.#submitBtn.textContent = 'Error — Try Again';

    setTimeout(() => {
      this.#submitBtn.style.borderColor = '';
      this.#submitBtn.textContent = 'Submit Application →';
    }, 3000);
  }

  #showSuccess() {
    if (!this.#form || !this.#successOverlay) return;
    this.#form.style.display = 'none';
    this.#successOverlay.style.display = 'block';
  }

  #resetForm() {
    if (!this.#form || !this.#successOverlay) return;
    this.#form.reset();
    this.#successOverlay.style.display = 'none';
    this.#form.style.display = 'flex';

    // Clear all error messages
    this.#form.querySelectorAll('.error-message').forEach(el => {
      el.textContent = '';
    });
  }

  #updateStats() {
    const requests = Storage.getFundingRequests();
    const approved = requests.filter(r => r.status === 'approved' || r.status === 'funded').length;

    const stat2 = document.getElementById('fundingStat2');
    if (stat2) stat2.textContent = approved.toString();
  }

  destroy() {
    this.#abortController.abort();
  }
}
